import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, Loader2, LogIn } from "lucide-react";
import { useAuth, formatApiError } from "../context/AuthContext";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back, Denisse.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6"
    >
      <div className="absolute inset-0 grain pointer-events-none opacity-40" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <img
            src="/assets/nova-logo.png"
            alt="NOVA MEDIA"
            className="w-full max-w-[260px] mb-6 select-none"
            draggable={false}
          />
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#7FB3D5] flex items-center gap-2">
            <Sparkle className="w-3 h-3" fill="#7FB3D5" strokeWidth={0.6} />
            Admin console
            <Sparkle className="w-3 h-3" fill="#7FB3D5" strokeWidth={0.6} />
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10 space-y-6"
        >
          <h1 className="font-display text-3xl md:text-4xl font-light leading-tight">
            Sign in to
            <span className="italic text-[#7FB3D5]"> your desk</span>.
          </h1>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              placeholder="denisse@novamediapr.com"
              data-testid="admin-login-email"
              className="w-full bg-transparent text-white placeholder:text-white/25 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light transition-colors"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-login-password"
              className="w-full bg-transparent text-white placeholder:text-white/25 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#7FB3D5] text-black text-[11px] uppercase tracking-[0.3em] hover:bg-white transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Signing in
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                Sign in
              </>
            )}
          </button>

          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 text-center pt-2">
            ✦ For authorized users only ✦
          </p>
        </form>

        <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
          <a href="/" className="hover:text-[#7FB3D5] transition-colors">
            ← Back to site
          </a>
        </p>
      </div>
    </main>
  );
}
